import type { ExternalError, IssueBlocker } from '@mes-amis/super-form';
import type { ServerFieldErrors } from '@/db/types/document';

/** Expand server field errors into the inline-validation map the SuperForm
 *  renderer consumes. Each error is registered under every dotted-path suffix
 *  of its slug so the inline mark matches regardless of how the form definition
 *  names the field (e.g. "person.client.address.zip" also registers
 *  "client.address.zip", "address.zip", "zip").
 *
 *  Fields are processed shallowest-slug-first (ties broken lexically), so when
 *  two fields would claim the same suffix key an explicitly shorter field owns
 *  its exact key and a deeper field's generated suffix can't overwrite it. The
 *  result is deterministic — independent of the server's response ordering. */
export function serverFieldErrorsToValidationMap(fieldErrors: ServerFieldErrors): Record<string, string> {
  const out: Record<string, string> = {};
  const bySpecificity = Object.entries(fieldErrors)
    .sort(([a], [b]) => a.split('.').length - b.split('.').length || a.localeCompare(b));
  for (const [field, { message }] of bySpecificity) {
    const segments = field.split('.');
    for (let i = 0; i < segments.length; i++) {
      const key = segments.slice(i).join('.');
      if (!out[key]) out[key] = message;
    }
  }
  return out;
}

/** Server field errors as the Blockers the issue controls list, one per field. */
export function serverFieldErrorsToBlockers(fieldErrors: ServerFieldErrors): IssueBlocker[] {
  return Object.entries(fieldErrors).map(([field, { message, label }]) => ({
    id: field,
    kind: 'field' as const,
    field,
    label: label || field,
    message,
  }));
}

/** A finalize error that lives on another record (`external_errors`) as a
 *  Blocker: a "fix it in the online app" link, or — without a path — one only
 *  the CS team can resolve. */
export function externalErrorBlocker(error: ExternalError, index: number): IssueBlocker {
  const message = error.messages?.join(' ') || 'Has an invalid value, but must be resolved in the online app.';
  if (error.path) {
    return { id: `external:${index}`, kind: 'external', label: error.source?.name || 'Client profile', message, href: error.path, linkText: error.link_text };
  }
  return { id: `system:${index}`, kind: 'system', label: 'Needs the CS team', message };
}
